import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { showSuccess } from "@/components/SuccessOverlay";
import { Home, LogIn, UserPlus, Loader2, User, Lock, Eye, EyeOff, KeyRound, Mail, ArrowLeft, Users } from "lucide-react";
import TenantBrand from "@/components/TenantBrand";
import HalloweenDecor from "@/components/HalloweenDecor";

export default function Login() {
  const { login, register } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  // Un link de referido (?ref=CODIGO) siempre implica que quieren registrarse,
  // sin importar qué diga ?mode -- y el código se guarda una sola vez aquí,
  // no en cada recarga (ver AGENTS/memoria: comisión de por vida, vínculo
  // permanente fijado al crear la cuenta).
  const refCode = params.get("ref") || "";
  const [mode, setMode] = useState(refCode || params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ username: "", password: "", confirm: "", referral_code: refCode });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Recuperar contraseña: paso 1 pide el código por correo (solo funciona si
  // esa cuenta ya vinculó y verificó un correo real -- ver /account/link-email
  // en Mi Perfil), paso 2 lo canjea por una contraseña nueva.
  const [forgotStep, setForgotStep] = useState("request"); // "request" | "reset"
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotPass, setForgotPass] = useState("");
  const [forgotConfirm, setForgotConfirm] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = () => { setMode(mode === "login" ? "register" : "login"); setErr(""); };
  const openForgot = () => { setForgotStep("request"); setForgotUsername(form.username); setForgotCode(""); setForgotPass(""); setForgotConfirm(""); setErr(""); setMode("forgot"); };
  const backToLogin = () => { setMode("login"); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "register" && form.password !== form.confirm) {
      setErr("Las contraseñas no coinciden");
      return;
    }
    setBusy(true);
    try {
      const u = mode === "login" ? await login(form.username, form.password)
        : await register({ username: form.username, password: form.password, referral_code: form.referral_code.trim() || null });
      showSuccess(`Bienvenido ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/");
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const submitForgotRequest = async (e) => {
    e.preventDefault();
    setErr("");
    if (!forgotUsername.trim()) return setErr("Escribe tu nombre de usuario");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { username: forgotUsername.trim() });
      showSuccess("Si esa cuenta tiene un correo vinculado, ahí te llegó un código.");
      setForgotStep("reset");
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const submitForgotReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (forgotPass !== forgotConfirm) return setErr("Las contraseñas no coinciden");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { username: forgotUsername.trim(), code: forgotCode.trim(), new_password: forgotPass });
      showSuccess("Contraseña actualizada. Ya puedes iniciar sesión.");
      setForm((f) => ({ ...f, username: forgotUsername.trim(), password: "" }));
      setMode("login");
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-40 bg-primary text-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 h-[70px] flex items-center justify-between">
          <TenantBrand tenant={tenant} imgClassName="h-10 w-auto object-contain" />
          <div className="flex items-center gap-2">
            <button data-testid="login-home" onClick={() => navigate("/")} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors">
              <Home size={16} /><span className="hidden sm:inline">Inicio</span>
            </button>
            {mode !== "forgot" && (
              <button data-testid="toggle-mode" onClick={toggle} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors">
                {mode === "login" ? <UserPlus size={16} /> : <LogIn size={16} />}
                <span className="hidden sm:inline">{mode === "login" ? "Registrarse" : "Iniciar Sesión"}</span>
              </button>
            )}
          </div>
        </div>
        <HalloweenDecor />
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            {mode === "forgot" ? (
              <>
                <button onClick={backToLogin} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-primary mb-4"><ArrowLeft size={15} /> Volver a iniciar sesión</button>
                <h2 className="font-head text-2xl font-extrabold text-slate-900 mb-2 text-center flex items-center justify-center gap-2"><KeyRound size={20} className="text-primary" /> Recuperar contraseña</h2>
                {forgotStep === "request" ? (
                  <>
                    <p className="text-sm text-slate-500 mb-6 text-center">Escribe tu usuario -- si tiene un correo vinculado, te mandamos un código ahí.</p>
                    <form onSubmit={submitForgotRequest} className="space-y-4">
                      <Field icon={User} label="Nombre de usuario" testid="forgot-username" value={forgotUsername} onChange={(e) => setForgotUsername(e.target.value)} placeholder="Tu nombre de usuario" required />
                      {err && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</p>}
                      <button data-testid="forgot-request-submit" disabled={busy}
                        className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3.5 transition-colors">
                        {busy ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />} Enviarme el código
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 mb-6 text-center">Escribe el código que te llegó por correo y tu contraseña nueva.</p>
                    <form onSubmit={submitForgotReset} className="space-y-4">
                      <Field icon={KeyRound} label="Código" testid="forgot-code" value={forgotCode} onChange={(e) => setForgotCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6} required />
                      <PasswordField label="Contraseña nueva" testid="forgot-password" value={forgotPass} onChange={(e) => setForgotPass(e.target.value)} placeholder="Crea una contraseña segura" required />
                      <PasswordField label="Confirmar contraseña" testid="forgot-confirm" value={forgotConfirm} onChange={(e) => setForgotConfirm(e.target.value)} placeholder="Repite tu contraseña" required />
                      {err && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</p>}
                      <button data-testid="forgot-reset-submit" disabled={busy}
                        className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3.5 transition-colors">
                        {busy ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={18} />} Cambiar contraseña
                      </button>
                      <button type="button" onClick={() => setForgotStep("request")} className="w-full text-xs text-slate-400 hover:text-primary">¿No te llegó? Pedir otro código</button>
                    </form>
                  </>
                )}
              </>
            ) : (
              <>
                <h2 className="font-head text-2xl font-extrabold text-slate-900 mb-6 text-center">
                  {mode === "login" ? "Iniciar Sesión" : "Registrarse"}
                </h2>
                <form onSubmit={submit} className="space-y-4">
                  <Field icon={User} label="Nombre de usuario" testid="login-username" value={form.username} onChange={upd("username")}
                    placeholder={mode === "register" ? "Elige un nombre de usuario único" : "Nombre de usuario"} required />
                  <div>
                    <PasswordField label="Contraseña" testid="login-password" value={form.password} onChange={upd("password")}
                      placeholder={mode === "register" ? "Crea una contraseña segura" : "Contraseña"} required />
                    {mode === "register" && <StrengthBar password={form.password} />}
                    {mode === "login" && (
                      <button type="button" data-testid="open-forgot" onClick={openForgot} className="text-xs text-primary font-semibold hover:underline mt-1.5">¿Olvidaste tu contraseña?</button>
                    )}
                  </div>
                  {mode === "register" && (
                    <PasswordField label="Confirmar contraseña" testid="reg-confirm" value={form.confirm} onChange={upd("confirm")}
                      placeholder="Repite tu contraseña" required />
                  )}
                  {mode === "register" && (
                    <Field icon={Users} label="Código de referido (opcional)" testid="reg-referral"
                      value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                      placeholder="Si alguien te invitó, escríbelo aquí" />
                  )}
                  {err && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</p>}
                  <button data-testid="auth-submit" disabled={busy}
                    className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3.5 transition-colors">
                    {busy ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? <LogIn size={18} /> : <UserPlus size={18} />}
                    {mode === "login" ? "Iniciar Sesión" : "Registrarse"}
                  </button>
                </form>
                <div className="border-t border-slate-100 mt-6 pt-5 text-center text-sm text-slate-500">
                  {mode === "login"
                    ? <>¿No tienes una cuenta? <button onClick={toggle} className="text-primary font-semibold hover:underline">Regístrate aquí</button></>
                    : <>¿Ya tienes una cuenta? <button onClick={toggle} className="text-primary font-semibold hover:underline">Inicia sesión aquí</button></>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-primary text-white text-center py-5 px-4">
        <p className="text-sm font-medium flex items-center justify-center gap-2 flex-wrap">
          ¿Necesitas una tienda web?
          <a data-testid="footer-whatsapp" href="https://wa.me/529372721690" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold underline hover:text-red-100 transition-colors">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M17.6 6.3A7.9 7.9 0 0 0 12 4a7.9 7.9 0 0 0-6.8 11.9L4 20l4.2-1.1A7.9 7.9 0 0 0 12 20a7.9 7.9 0 0 0 5.6-13.7ZM12 18.5c-1.2 0-2.4-.3-3.4-.9l-.2-.1-2.5.7.7-2.4-.2-.3a6.6 6.6 0 1 1 5.6 3ZM15.6 13.8c-.2-.1-1.2-.6-1.3-.6-.2-.1-.3-.1-.4.1s-.5.6-.6.7-.2.1-.4 0a5.3 5.3 0 0 1-2.6-2.3c-.2-.3.2-.3.5-1 .1-.1 0-.3 0-.4l-.6-1.4c-.2-.4-.3-.3-.4-.3h-.4c-.1 0-.4.1-.6.3-.2.2-.8.8-.8 1.9s.8 2.2.9 2.4c.1.1 1.6 2.5 3.9 3.4.5.2 1 .4 1.3.5.5.2 1 .1 1.4.1.4-.1 1.2-.5 1.3-1s.2-.9.1-1c0-.1-.1-.2-.3-.3Z"/></svg>
            WhatsApp
          </a>
          <span className="opacity-60">|</span>
          <a data-testid="footer-telegram" href="https://t.me/Martin04020" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold underline hover:text-red-100 transition-colors">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M21.9 4.3 2.9 11.6c-1.1.4-1.1 1.6 0 1.9l4.8 1.5 1.8 5.7c.3.8.9.9 1.4.3l2.7-2.6 4.7 3.5c.7.4 1.4.2 1.6-.7l3-14.1c.3-1.1-.5-1.7-1.2-1.5ZM8.9 14.3l9.2-5.8c.4-.3.8.1.5.4l-7.5 6.8-.3 3.2-1.4-4.6Z"/></svg>
            Telegram
          </a>
        </p>
        <p className="text-red-200 text-xs mt-1">© 2026 MQ STORE. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}

function StrengthBar({ password }) {
  const score = useMemo(() => {
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 10) s++;
    if (/[A-Z]/.test(password) && /[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  }, [password]);
  const colors = ["bg-red-400", "bg-red-400", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"];
  return (
    <div className="flex gap-1 mt-1.5">
      {[0, 1, 2, 3].map((i) => <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < score ? colors[score] : "bg-slate-100"}`} />)}
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...props }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-1.5"><Icon size={15} className="text-primary" /> {label}</span>
      <input data-testid={testid} {...props}
        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-colors" />
    </label>
  );
}
function PasswordField({ label, testid, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-1.5"><Lock size={15} className="text-primary" /> {label}</span>
      <div className="relative">
        <input data-testid={testid} {...props} type={show ? "text" : "password"}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-4 pr-11 py-3 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-colors" />
        <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors">
          {show ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </div>
    </label>
  );
}
